import React, { ElementType } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Trans, useTranslation } from 'react-i18next'
import Typography from '@mui/material/Typography'
import { ConnectorErrorType, ConnectorModalType } from '@typesApp/Connector'
import {
  clearConnectorError,
  connectorBusySelector,
  connectorErrorSelector,
  connectorModalSelector,
  hideConnectorModal,
} from '@redux/slices/connectorSlice'
import { useEnvironment } from '@hooks/useEnvironment'
import { Modal } from '@components/ui/Modal/Modal'
import { ConfirmationModal } from '@components/ui/Modal/ConfirmationModal'
import { Icon } from '@components/ui/Icon/Icon'
import { LoaderWithBackdrop } from '@components/ui-css/Loader'
import { StyledAnchor } from '@components/ui/Anchor/StyledAnchor'
import { usePageFlow } from '@hooks/usePageFlow'
import { StyledButton } from '@components/ui/Button'
import { injectDataAttributes } from '@utils/dataAttributes'

const SUPPRESS_MODAL_ERRORS = [ConnectorErrorType.NONE, ConnectorErrorType.CONFIGURATION_ERROR]

export const CartInterceptListener: React.FC = () => {
  const dispatch = useDispatch()
  const isBusy = useSelector(connectorBusySelector)
  const modal = useSelector(connectorModalSelector)

  const onConfirm = () => {
    dispatch(hideConnectorModal())
    if (modal.confirmCallback) {
      modal.confirmCallback()
    }
  }

  const onDismiss = () => {
    dispatch(hideConnectorModal())
  }

  const modalMap = {
    ADD_ITEM: EditCartConfirmationModal,
    EDIT_ITEM: EditCartConfirmationModal,
    REMOVE_ITEM: EditCartConfirmationModal,
    CHANGE_QUANTITY: EditCartConfirmationModal,
    APPLY_PROMO_CODE: EditCartConfirmationModal,
    REMOVE_PROMO_CODE: EditCartConfirmationModal,
    APPLY_SUBSCRIPTION: EditCartConfirmationModal,
    QUOTE_CANCELLED: BasicConfirmationModal,
  } as Record<ConnectorModalType, ElementType>

  const InterceptModal = modalMap[modal.type]
  const keyPrefix = modal.type.toLowerCase().replaceAll('_', '-')

  return (
    <>
      <LoaderWithBackdrop open={isBusy} />
      {InterceptModal && <InterceptModal onConfirm={onConfirm} onDismiss={onDismiss} keyPrefix={keyPrefix} />}
      <ErrorListener />
    </>
  )
}

interface InterceptModalProps {
  onConfirm: () => void
  onDismiss: () => void
  keyPrefix: string
}

const EditCartConfirmationModal: React.FC<InterceptModalProps> = props => {
  const { onConfirm, onDismiss, keyPrefix } = props
  const { translationNamespace } = useEnvironment()
  const { t } = useTranslation(translationNamespace, { keyPrefix: `InterceptListener.${keyPrefix}` })

  return (
    <ConfirmationModal
      open
      title={t('title')}
      message={t('message')}
      confirmLabel={t('cta-yes')}
      dismissLabel={t('cta-no')}
      onConfirm={onConfirm}
      onDismiss={onDismiss}
    />
  )
}

const BasicConfirmationModal: React.FC<InterceptModalProps> = props => {
  const { onConfirm, keyPrefix } = props
  const dispatch = useDispatch()
  const { translationNamespace } = useEnvironment()
  const { t } = useTranslation(translationNamespace, { keyPrefix: `InterceptListener.${keyPrefix}` })
  const { modalOptions } = usePageFlow()

  const onClose = () => {
    dispatch(hideConnectorModal())
    onConfirm?.()
  }

  return (
    <Modal
      open
      onClose={onClose}
      title={t('title')}
      footer={
        <StyledButton onClick={onClose} aria-label="close" {...injectDataAttributes('modal.close')}>
          {t('cta-close')}
        </StyledButton>
      }
    >
      <Typography textAlign={modalOptions?.textAlign} dangerouslySetInnerHTML={{ __html: t('message') }}></Typography>
    </Modal>
  )
}

const ErrorListener: React.FC = () => {
  const { translationNamespace } = useEnvironment()
  const { t } = useTranslation(translationNamespace)
  const dispatch = useDispatch()
  const error = useSelector(connectorErrorSelector)
  const { modalOptions } = usePageFlow()

  const onClose = () => {
    dispatch(clearConnectorError())
  }

  const translationMap = {
    [ConnectorErrorType.GENERIC_ERROR]: {
      heading: 'Common.GenericError.title',
      message: 'Common.GenericError.message',
    },
    [ConnectorErrorType.NULLIFY_ERROR]: {
      heading: 'DirectBilling.Checkout.NullifyRejectedError.Title',
      message: 'DirectBilling.Checkout.NullifyRejectedError.Message',
    },
  } as Record<string, { heading: string; message: string }>

  const contentMap = {
    [ConnectorErrorType.GENERIC_ERROR]:
      error === ConnectorErrorType.GENERIC_ERROR
        ? {
            phone_anchor: (
              <StyledAnchor external target={''} to={`tel:${t('ContactInfo.phone')}`}>
                <Icon name="call-icon" marginRight />
                {t('ContactInfo.phone-label')}
              </StyledAnchor>
            ),
          }
        : {},
  } as Record<string, Record<string, JSX.Element>>

  return (
    <Modal
      open={error && !SUPPRESS_MODAL_ERRORS.includes(error)}
      onClose={onClose}
      title={t(translationMap[error]?.heading)}
      footer={
        <StyledButton onClick={onClose} aria-label="close" {...injectDataAttributes('modal.close')}>
          {t('Common.GenericError.cta-close')}
        </StyledButton>
      }
    >
      <Typography textAlign={modalOptions?.textAlign}>
        <Trans ns={translationNamespace} i18nKey={translationMap[error]?.message} components={contentMap[error]} />
      </Typography>
    </Modal>
  )
}
